import React, { useEffect, useRef, useState } from "react";
import {
  Link as RouterLink,
  Route,
  Routes,
  useParams,
  useLocation,
} from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FruitifyLogo from "../../shared-components/FruitifyLogo";
import { useAuthenticationStore } from "../../stores/auth";
import { helpContent } from "./content";
import { publishedContent } from "./manifest";
import { guidePath, resolveHelpAction } from "./actions";

import { SampleMarkedAnswer } from "./examples/SampleMarkedAnswer";

const guides = publishedContent(helpContent);
const availabilityLabels = {
  general: "All users",
  teacher: "Teacher feature",
  "approved-school": "Approved-school feature",
};

function useTitle(title) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} | Fruitify Help`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

function GuideMetadata({ guide }) {
  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      flexWrap="wrap"
      sx={{ my: 2 }}
    >
      <Chip label={availabilityLabels[guide.availability]} size="small" />
      {guide.audiences.map((audience) => (
        <Chip key={audience} label={audience} size="small" variant="outlined" />
      ))}
      {guide.durationMinutes && (
        <Typography variant="body2">{guide.durationMinutes} min</Typography>
      )}
    </Stack>
  );
}

function Catalogue({ entries }) {
  useTitle("Help and tutorials");
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const matches = entries.filter((guide) =>
    [guide.title, guide.summary, guide.outcome, ...guide.keywords]
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
  return (
    <>
      <Typography component="h1" variant="h3" gutterBottom>
        Help and tutorials
      </Typography>
      <Typography sx={{ mb: 3 }}>
        Explore guides and examples at your own pace.
      </Typography>
      <TextField
        fullWidth
        label="Search guides"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        type="search"
        sx={{ mb: 3 }}
      />
      {!entries.length ? (
        <Typography role="status">
          Tutorials are being prepared. Please check back soon.
        </Typography>
      ) : !matches.length ? (
        <Box role="status">
          <Typography>No guides match your search.</Typography>
          <Button onClick={() => setSearch("")}>Clear search</Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          {matches.map((guide) => (
            <Card
              key={guide.id}
              variant="outlined"
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography component="h2" variant="h5">
                  {guide.title}
                </Typography>
                <GuideMetadata guide={guide} />
                <Typography>{guide.summary}</Typography>
              </CardContent>
              <CardActions>
                <Button
                  component={RouterLink}
                  to={guidePath(guide)}
                  aria-label={`Read ${guide.title}`}
                >
                  Read {guide.kind === "example" ? "example" : "guide"}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
    </>
  );
}

function Screenshot({ screenshot }) {
  const [failed, setFailed] = useState(false);
  return (
    <Box component="figure" sx={{ mx: 0, my: 2 }}>
      {failed ? (
        <Typography role="status">
          Screenshot unavailable: {screenshot.alt}
        </Typography>
      ) : (
        <Link
          href={screenshot.src}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open full-size screenshot: ${screenshot.alt}`}
          underline="none"
          sx={{ display: "block", "&::before": { content: "none" } }}
        >
          <Box
            component="img"
            src={screenshot.src}
            alt={screenshot.alt}
            loading="lazy"
            onError={() => setFailed(true)}
            sx={{
              display: "block",
              width: "100%",
              height: "auto",
              borderRadius: 1,
            }}
          />
        </Link>
      )}
      {screenshot.caption && (
        <Typography
          component="figcaption"
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          {screenshot.caption}
        </Typography>
      )}
    </Box>
  );
}

function GuideVideo({ video }) {
  const [failed, setFailed] = useState(false);
  return (
    <Box sx={{ my: 3 }}>
      {failed ? (
        <Typography role="status">
          Video unavailable. You can follow the steps or read the transcript
          below.
        </Typography>
      ) : (
        <Box
          component="video"
          aria-label="Tutorial video"
          controls
          preload="metadata"
          crossOrigin="anonymous"
          src={video.src}
          poster={video.poster}
          onError={() => setFailed(true)}
          sx={{
            display: "block",
            width: "100%",
            maxHeight: "70vh",
            borderRadius: 1,
            bgcolor: "common.black",
          }}
        >
          <track
            kind="captions"
            src={video.captions.src}
            srcLang={video.captions.language}
            label={video.captions.label}
            default
          />
          Your browser cannot play this video. Read the transcript below.
        </Box>
      )}
      <Box component="details" sx={{ mt: 2 }}>
        <Box component="summary" sx={{ cursor: "pointer" }}>
          Read transcript
        </Box>
        <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
          {video.transcript}
        </Typography>
      </Box>
    </Box>
  );
}

function MissingGuide() {
  useTitle("Guide not found");
  return (
    <>
      <Typography component="h1" variant="h4" gutterBottom>
        Guide not found
      </Typography>
      <Typography sx={{ mb: 2 }}>
        This guide may not be available yet.
      </Typography>
      <Button component={RouterLink} to="/help">
        Browse help
      </Button>
    </>
  );
}

function Guide({ entries, kind }) {
  const { slug } = useParams();
  const guide = entries.find(
    (entry) => entry.slug === slug && entry.kind === kind,
  );
  return guide ? (
    <GuideContent key={guide.id} guide={guide} entries={entries} />
  ) : (
    <MissingGuide />
  );
}

function GuideContent({ guide, entries }) {
  useTitle(guide.title);
  const { user } = useAuthenticationStore();
  const action = resolveHelpAction(guide, user);
  const related = entries.filter((entry) => guide.related.includes(entry.id));
  return (
    <Box component="article" sx={{ maxWidth: 960, mx: "auto" }}>
      <Link component={RouterLink} to="/help">
        All guides
      </Link>
      <Typography component="h1" variant="h3" sx={{ mt: 2 }}>
        {guide.title}
      </Typography>
      {guide.kind === "example" && <Chip label="Sample data" sx={{ mt: 2 }} />}
      <GuideMetadata guide={guide} />
      <Typography variant="h6" component="p">
        {guide.outcome}
      </Typography>
      <Typography sx={{ my: 2 }}>{guide.summary}</Typography>
      {guide.interactiveExample === "marked-answer" && (
        <Link href="#explore-answer">Explore the sample answer</Link>
      )}
      {guide.leadScreenshot && <Screenshot screenshot={guide.leadScreenshot} />}
      {guide.video && (
        <>
          <GuideVideo video={guide.video} />
          <Typography sx={{ my: 2 }}>
            Watch the video, follow the step-by-step guide below, or use both.
          </Typography>
        </>
      )}
      <Box
        component="details"
        sx={{ my: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
      >
        <Box component="summary" sx={{ cursor: "pointer" }}>
          On this page
        </Box>
        <Box component="nav" aria-label="Guide steps">
          <Box component="ol" sx={{ pl: 3 }}>
            {guide.interactiveExample === "marked-answer" && (
              <li>
                <Link href="#explore-answer">Explore the sample answer</Link>
              </li>
            )}
            {guide.steps.map((step) => (
              <li key={step.id}>
                <Link href={`#${step.id}`}>{step.title}</Link>
              </li>
            ))}
          </Box>
        </Box>
      </Box>
      {guide.interactiveExample === "marked-answer" && <SampleMarkedAnswer />}
      <Box component="ol" sx={{ pl: 3, my: 0 }}>
        {guide.steps.map((step) => (
          <Box
            component="li"
            key={step.id}
            id={step.id}
            sx={{
              mb: { xs: 4, md: 6 },
              pl: { xs: 0, md: 1 },
              scrollMarginTop: 100,
              "&::marker": { fontWeight: 600 },
              "&:not(:last-child)": {
                pb: 3,
                borderBottom: 1,
                borderColor: "divider",
              },
            }}
          >
            <Typography component="h2" variant="h5">
              {step.title}
            </Typography>
            <Typography sx={{ mt: 1, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
              {step.body}
            </Typography>
            {step.screenshot && <Screenshot screenshot={step.screenshot} />}
          </Box>
        ))}
      </Box>
      {guide.downloads?.map((download) => (
        <Box key={download.src} sx={{ my: 2 }}>
          <Link href={download.src}>{download.label}</Link>
          <Typography variant="body2">{download.description}</Typography>
        </Box>
      ))}
      {action && (
        <Box sx={{ my: 3 }}>
          {action.explanation && (
            <Typography sx={{ mb: 1 }}>{action.explanation}</Typography>
          )}
          <Button variant="contained" component={RouterLink} to={action.path}>
            {action.label}
          </Button>
        </Box>
      )}
      {!!related.length && (
        <Box sx={{ my: 3 }}>
          <Typography component="h2" variant="h5">
            Related guides
          </Typography>
          <Stack gap={1} sx={{ mt: 1 }}>
            {related.map((entry) => (
              <Link key={entry.id} component={RouterLink} to={guidePath(entry)}>
                {entry.title}
              </Link>
            ))}
          </Stack>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        Reviewed {guide.reviewedAt} · Fruitify {guide.reviewedRelease} · Guide{" "}
        {guide.version}
      </Typography>
    </Box>
  );
}

export function HelpRoutes({ entries = guides }) {
  const mainRef = useRef(null);
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const candidate = document.getElementById(hash.slice(1));
    const step =
      candidate && mainRef.current?.contains(candidate) ? candidate : null;
    const target = step || mainRef.current?.querySelector("h1");
    target?.setAttribute("tabindex", "-1");
    target?.focus({ preventScroll: Boolean(step) });
    step?.scrollIntoView({ block: "start" });
  }, [pathname, hash]);
  const { user } = useAuthenticationStore();
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Stack
        component="header"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        sx={{ mb: { xs: 3, md: 5 } }}
      >
        <Link
          component={RouterLink}
          to="/help"
          aria-label="Fruitify Help home"
          underline="none"
          sx={{ display: "inline-flex", "&::before": { content: "none" } }}
        >
          <FruitifyLogo horizontal width={140} />
        </Link>
        <Stack direction="row" gap={1}>
          <Button component={RouterLink} to="/help">
            Help home
          </Button>
          <Button
            component={RouterLink}
            to={user?.isLoggedIn ? "/" : "/auth/login"}
          >
            {user?.isLoggedIn ? "Return to Fruitify" : "Sign in"}
          </Button>
        </Stack>
      </Stack>
      {/* Headings and guide steps receive navigation focus for screen readers. */}
      <Box
        component="main"
        ref={mainRef}
        sx={{ '& [tabindex="-1"]:focus': { outline: "none" } }}
      >
        <Routes>
          <Route index element={<Catalogue entries={entries} />} />
          <Route
            path="tutorials/:slug"
            element={<Guide entries={entries} kind="tutorial" />}
          />
          <Route
            path="examples/:slug"
            element={<Guide entries={entries} kind="example" />}
          />
          <Route path="*" element={<MissingGuide />} />
        </Routes>
      </Box>
    </Container>
  );
}
